import {
  type AgentSubscriber,
  type Message
} from "@ag-ui/client";
import { AiChatAssistantTranslation, DEFAULT_CONTEXT } from "app/components/ai-chat-assistant/constants";
import { CaseDraftPayload, CaseDraftSnapShot, ChatMessage } from "app/components/ai-chat-assistant/types";
import { ApplicationState } from "app/types-store";
import { useCallback, useState, useRef, useEffect, RefObject, DependencyList } from "react";
import * as React from 'react';
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { actionCreators } from "app/components/ai-chat-assistant/actions";
import { useRunGuard } from "app/components/ai-chat-assistant/hooks/use-run-guard";
import { useAiConfirmation } from "app/components/ai-chat-assistant/hooks/use-case-draft-confirmation";
import { createAiChatAssistantMessage, createUserMessage, toAgentMessage, 
  useChatMessages } from "app/components/ai-chat-assistant/hooks/use-ai-chat-assistant-messages";
import { useAiChatAssistantSelectors } from "app/components/ai-chat-assistant/hooks/use-ai-chat-assistant-selectors";
import { useTranslation } from "app/infrastructure/translate/hooks";
import { STATE } from "app/constants";
import { ExecuteAgUiRunArgs, CreateAgUiSubscriberArgs, 
  SendMessageArgs, StreamState, 
  DraftAction} from "app/components/ai-chat-assistant/hooks/use-ai-chat-assistant-types";
import { createCaseDraftAction } from "app/components/ai-chat-assistant/hooks/draft-action";

function useAutoScroll(
  endRef: RefObject<HTMLDivElement | null>,
  deps: DependencyList
) {
  useEffect(() => {
    endRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end"
    });
  }, deps);
}

function createAgUiSubscriber({
  requestId,
  aiChatAssistantMessageId,
  streamState,
  isActiveRun,
  updateMessage,
  handleStateSnapshot,
  handleConfirmedDraft,
  translation
}: CreateAgUiSubscriberArgs): AgentSubscriber {
  return {
    onStateSnapshotEvent: ({ event }) => {
      handleStateSnapshot(event as { type: string; snapshot?: CaseDraftSnapShot });
    },

    onTextMessageStartEvent: () => {
      if (streamState.fullText.length > 0) {
        streamState.fullText += "\n\n";
      }
    },

    onTextMessageContentEvent: ({ event }) => {
      if (!isActiveRun(requestId)) {
        return;
      }

      streamState.fullText += event.delta ?? "";
      updateMessage(aiChatAssistantMessageId, streamState.fullText);
    },

    onRunFailed: () => {
      if (!isActiveRun(requestId)) {
        return;
      }

      updateMessage(
        aiChatAssistantMessageId,
        translation.textAiChatAssistantEndpointError
      );
    },

    onRunFinishedEvent: async () => {
      if (!isActiveRun(requestId)) {
        return;
      }

      streamState.handledDraft = await handleConfirmedDraft();
    }
  };
}

function validateAndPrepareMessage(
  value: string,
  running: boolean,
  tenant: string | null
): { valid: boolean; requestId?: string } {
  if (!value || running) {
    return { valid: false };
  }

  if (!tenant) {
    return { valid: false };
  }

  return { valid: true };
}

function buildOutboundMessages(
  messagesRef: RefObject<ChatMessage[]>,
  userMessage: ChatMessage
): Message[] {
  return [
    ...messagesRef.current,
    userMessage
  ].map(toAgentMessage);
}

async function executeAgUiRun({
  dispatch,
  tenant,
  context,
  threadId,
  outboundMessages,
  subscriber,
  isActiveRun,
  requestId,
  streamState,
  aiChatAssistantMessage,
  updateMessage,
  translation,
  abortController
}: ExecuteAgUiRunArgs): Promise<void> {
  await dispatch(
    actionCreators.executeAiAssistantRun({
      tenant,
      context,
      threadId,
      messages: outboundMessages,
      subscriber,
      abortController
    })
  );

  if (!isActiveRun(requestId)) {
    return;
  }

  if (!streamState.fullText.trim() && !streamState.handledDraft) {
    updateMessage(aiChatAssistantMessage.id, translation.textNoResponse);
  }
}

function useSendMessage({
  inputText,
  setInputText,
  running,
  tenant,
  context,
  threadId,
  messagesRef,
  appendMessages,
  updateMessage,
  startRun,
  isActiveRun,
  resetDraftConfirmation,
  handleStateSnapshot,
  handleConfirmedDraft,
  dispatch,
  translation
}: SendMessageArgs) {
  return useCallback(async () => {
    const value = inputText.trim();

    const userMessage = createUserMessage(value);
    const aiChatAssistantMessage = createAiChatAssistantMessage();

    const validation = validateAndPrepareMessage(
      value,
      running,
      tenant
    );

    if (!validation.valid) {
      updateMessage(aiChatAssistantMessage.id, translation.textAiChatAssistantEndpointError);
      return;
    }

    const { runId: requestId, abortController } = startRun();
    setInputText("");
    appendMessages([userMessage, aiChatAssistantMessage]);
    resetDraftConfirmation();

    const outboundMessages = buildOutboundMessages(messagesRef, userMessage);

    const streamState: StreamState = {
      fullText: ""
    };

    const subscriber = createAgUiSubscriber({
      requestId, aiChatAssistantMessageId: aiChatAssistantMessage.id, streamState, isActiveRun,
      updateMessage, handleStateSnapshot, handleConfirmedDraft, translation
    });

    await executeAgUiRun({
      dispatch, 
      tenant, 
      context,
      threadId,
      outboundMessages,
      subscriber, isActiveRun, requestId, streamState,
      aiChatAssistantMessage, updateMessage, translation, abortController
    });
  }, [
    inputText, running, tenant, context, threadId, messagesRef, appendMessages, updateMessage,
    startRun, isActiveRun, resetDraftConfirmation, handleStateSnapshot, handleConfirmedDraft,
     dispatch, setInputText, translation
  ]);
}

function useInitializeHookDependencies(caseTypeRequired, aiChatFeatureFlag) {
  const dispatch: ThunkDispatch<ApplicationState, any, any> = useDispatch();
  const [inputText, setInputText] = useState("");
  const endRef = useRef<HTMLDivElement | null>(null);

  const { enableAiAdvisor, caseDraftCreation, tenant, running, createDraft, 
    updateDraft, saveDraft, aiChatFeature } = useAiChatAssistantSelectors(aiChatFeatureFlag);
  const translation = useTranslation(STATE.aiChatAssistant, AiChatAssistantTranslation);

  const {
    messages,
    messagesRef,
    threadId,
    appendMessages,
    updateMessage,
    clearMessages
  } = useChatMessages();

  const {
    startRun,
    isActiveRun,
    cancelCurrentRun,
    resetRunState
  } = useRunGuard();

  const {
    resetDraftConfirmation,
    handleStateSnapshot,
    consumeConfirmedDraft,
    consumeLatestDraft,
    canActOnDraft
  } = useAiConfirmation({ caseTypeRequired });

  return {
    dispatch, inputText, setInputText, endRef, enableAiAdvisor, caseDraftCreation, tenant, running,
    messages, messagesRef, threadId, appendMessages, updateMessage, clearMessages,
    startRun, 
    isActiveRun, 
    cancelCurrentRun, 
    resetDraftConfirmation, 
    handleStateSnapshot, 
    consumeConfirmedDraft, 
    consumeLatestDraft, 
    canActOnDraft,
    translation,
    resetRunState,
    createDraft,
    updateDraft,
    saveDraft,
    aiChatFeature
  };
}

function useDraftHandler({
  consumeDraft,
  dispatch,
  updateMessage,
  draftAction,
  translation,
  dataTag
}: {
  consumeDraft: () => CaseDraftPayload | null;
  dispatch: ThunkDispatch<ApplicationState, any, any>;
  updateMessage: (id: string, text: string) => void;
  draftAction: DraftAction;
  translation: Record<string, string>;
  dataTag?: string;
}) {
  return useCallback(async () => {
    const draft = consumeDraft();

    if (!draft) {
      return false;
    }

    await draftAction(draft, {
      dispatch,
      updateMessage,
      translation,
      ...(dataTag ? { dataTag: dataTag ?? "" } : {})
    });
    
    return true;
  }, [consumeDraft, dispatch, updateMessage, translation]);
}

function useKeyDownHandler(send: () => void, inputText: string, running: boolean, setInputText: (text: string) => void) {
  return useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!running && inputText.trim()) {
          const target = e.currentTarget;
          send();
          target?.blur();
        }
      }
    },
    [send, inputText, running, setInputText]
  );
}

export function useAiChatAssistantHooks(
  context = DEFAULT_CONTEXT, 
  caseTypeRequired = true,
  draftAction: DraftAction = createCaseDraftAction,
  dataTag?: string,
  aiChatFeatureFlag?: string
) {
  const {
    dispatch,
    inputText,
    setInputText,
    endRef,
    enableAiAdvisor,
    caseDraftCreation,
    tenant,
    running,
    messages,
    messagesRef,
    threadId,
    appendMessages,
    updateMessage,
    clearMessages,
    startRun,
    isActiveRun,
    cancelCurrentRun,
    resetDraftConfirmation,
    handleStateSnapshot,
    consumeConfirmedDraft,
    consumeLatestDraft,
    canActOnDraft,
    translation,
    resetRunState,
    createDraft,
    updateDraft,
    saveDraft,
    aiChatFeature
  } = useInitializeHookDependencies(caseTypeRequired, aiChatFeatureFlag);

  useAutoScroll(endRef, [messages, running]);

  const handleConfirmedDraft = useDraftHandler({
    consumeDraft: consumeConfirmedDraft,
    dispatch,
    updateMessage,
    draftAction,
    translation,
    dataTag
  });

  const registerCase = useDraftHandler({
    consumeDraft: consumeLatestDraft,
    dispatch,
    updateMessage,
    draftAction,
    translation,
    dataTag
  });

  const send = useSendMessage({
    inputText, setInputText, running, tenant, context, threadId, messagesRef, appendMessages, updateMessage,
    startRun, isActiveRun, resetDraftConfirmation, handleStateSnapshot, handleConfirmedDraft, dispatch,
    translation
  });

  const clearChat = useCallback(() => {
    cancelCurrentRun();
    resetDraftConfirmation();
    clearMessages();
    resetRunState();
    setInputText("");
    dispatch(actionCreators.abortAiAssistantRun());
  }, [cancelCurrentRun, resetDraftConfirmation, clearMessages, resetRunState, setInputText]);

  const abortChat = useCallback(() => {
    cancelCurrentRun();
    resetDraftConfirmation();
    dispatch(actionCreators.abortAiAssistantRun());
  }, [cancelCurrentRun, resetDraftConfirmation, dispatch]);

  const onKeyDown = useKeyDownHandler(send, inputText, running, setInputText);

  return {
    enableAiAdvisor,
    caseDraftCreation,
    running,
    messages,
    inputText,
    setInputText,
    send,
    onKeyDown,
    endRef,
    clearChat,
    abortChat,
    translation,
    canActOnDraft,
    registerCase,
    createDraft,
    updateDraft,
    saveDraft,
    aiChatFeature
  };
}
